import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { useVideoContext } from './Video';

interface OverlayImageAttr {
  image?: Blob;
  position?: React.CSSProperties;
}

interface Props {
  start?: number; // seconds
  end?: number; // seconds
  headingTitle?: string;
  headingStyle?: React.CSSProperties;
  headingContainerStyle?: React.CSSProperties;
  logo?: string;
  imageAtr?: OverlayImageAttr;
  addLogo?: boolean;
}

// A lightweight HLS-aware player that clamps playback between start and end.
// - Uses native HLS on Safari, hls.js elsewhere.
// - Prevents scrubbing/rewinding before `start` and past `end`.
const HlsClippedPlayer: FC<Props> = ({
  start = 0,
  end,
  headingTitle,
  headingStyle,
  headingContainerStyle,
  logo,
  imageAtr,
  addLogo,
}) => {
  const context = useVideoContext();
  const { videoUrl } = context;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [segDuration, setSegDuration] = useState<number>(Math.max(0, (end ?? 0) - (start || 0)));
  const [currentSegTime, setCurrentSegTime] = useState<number>(0);

  // Attach/detach media source
  useEffect((): void | (() => void) => {
    const video = videoRef.current;
    if (!video || !videoUrl) return undefined;

    const isHls =
      /\.m3u8($|\?)/i.test(videoUrl) ||
      videoUrl.includes('format=m3u8') ||
      videoUrl.includes('application/vnd.apple.mpegurl');

    const setupNative = (): void => {
      video.src = videoUrl;
    };

    if (isHls) {
      const canNative = video.canPlayType('application/vnd.apple.mpegurl');
      if (canNative) {
        setupNative();
      } else if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        hlsRef.current = hls;
      } else {
        // Fallback: try setting src directly
        setupNative();
      }
    } else {
      setupNative();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute('src');
      try {
        video.load();
      } catch (e) {
        // ignore
      }
    };
  }, [videoUrl]);

  // Enforce clamped playback range and initial position
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const clamp = (t: number): number => {
      const s = Math.max(0, start || 0);
      const e = end && end > 0 ? end : Number.POSITIVE_INFINITY;
      if (t < s) return s;
      if (t > e) return e;
      return t;
    };

    const handleLoadedMetadata = (): void => {
      // Seek to the start of the segment
      try {
        video.currentTime = clamp(start || 0);
      } catch (_) {
        // some browsers require a play() before precise seek; ignore
      }
      const computedEnd = end != null ? end : isFinite(video.duration) ? video.duration : undefined;
      if (computedEnd != null) {
        setSegDuration(Math.max(0, computedEnd - (start || 0)));
      }
      setCurrentSegTime(0);
    };

    const handleSeeking = (): void => {
      const desired = clamp(video.currentTime);
      if (Math.abs(desired - video.currentTime) > 0.01) {
        video.currentTime = desired;
      }
    };

    const handleTimeUpdate = (): void => {
      if (end != null && video.currentTime >= end) {
        // Halt playback exactly at end boundary
        video.currentTime = end;
        video.pause();
        setIsPlaying(false);
      }
      // Prevent drifting before start due to keyboard shortcuts or programmatic seeks
      if (video.currentTime < (start || 0)) {
        video.currentTime = start || 0;
      }
      const within = Math.max(0, video.currentTime - (start || 0));
      setCurrentSegTime(within);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [start, end]);

  // Derived image URL for overlay if Blob provided
  const overlayImgUrl = imageAtr?.image ? URL.createObjectURL(imageAtr.image) : undefined;

  // Sync isPlaying with element state (e.g., if user agent auto-pauses)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = (): void => setIsPlaying(true);
    const onPause = (): void => setIsPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return (): void => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      const s = start || 0;
      const e = end ?? Number.POSITIVE_INFINITY;
      if (video.currentTime >= e - 0.01) {
        video.currentTime = s;
      }
      void video.play();
      setIsPlaying(true);
    }
  }, [isPlaying, start, end]);

  const onScrub = useCallback(
    (value: number): void => {
      const video = videoRef.current;
      if (!video) return;
      const s = start || 0;
      const duration = segDuration || 0;
      const v = Math.max(0, Math.min(duration, value));
      video.currentTime = s + v;
      setCurrentSegTime(v);
    },
    [start, segDuration],
  );

  const fmt = useCallback((t: number): string => {
    if (!isFinite(t)) return '0:00';
    const sign = t < 0 ? '-' : '';
    const abs = Math.max(0, Math.floor(Math.abs(t)));
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${sign}${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  const durationLabel = useMemo(() => fmt(segDuration || 0), [fmt, segDuration]);
  const currentLabel = useMemo(() => fmt(currentSegTime || 0), [fmt, currentSegTime]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'center' }}>
      {headingTitle ? (
        <div style={{ width: '100%', ...headingContainerStyle }}>
          <div style={headingStyle}>{headingTitle}</div>
        </div>
      ) : null}

      <div style={{ position: 'relative', display: 'inline-block' }}>
        <video
          ref={videoRef}
          controls={false}
          playsInline
          // Preload metadata to be able to seek to `start` quickly
          preload="metadata"
          style={{ maxHeight: '50vh', display: 'block' }}
        />

        {addLogo && (overlayImgUrl || logo) ? (
          <img
            src={logo ?? overlayImgUrl}
            alt="overlay"
            style={{ imageRendering: 'pixelated', ...(imageAtr?.position || {}) }}
          />
        ) : null}
      </div>

      {/* Minimal custom controls showing only segment duration */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, width: '100%' }}>
        <button type="button" onClick={togglePlay} style={{ padding: '4px 8px' }}>
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{currentLabel}</span>
        <input
          type="range"
          min={0}
          max={Math.max(0.01, segDuration || 0)}
          step={0.1}
          value={Math.min(currentSegTime, segDuration || 0)}
          onChange={(e) => onScrub(parseFloat(e.target.value))}
          style={{ flex: 1 }}
        />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{durationLabel}</span>
      </div>
    </div>
  );
};

export default HlsClippedPlayer;
